/**
 * Builds the HTML body for a credit note notification email.
 *
 * @param {object} cn  - Credit note data from the API createFromBookings response.
 *   Expected fields: creditNoteNumber, talentName, agencyName, agencyPercentage,
 *                    netAmount, vatAmount, grossAmount, bookingDates, invoiceDate,
 *                    invoiceType, createdBy
 * @returns {string} HTML string
 */
export function buildCreditNoteEmailHtml(cn) {
  const number      = cn.creditNoteNumber  || cn.credit_note_number  || '—';
  const talentName  = cn.talentName        || cn.talent_name         || '—';
  const agencyName  = cn.agencyName        || cn.agency_name         || null;
  const agencyPct   = cn.agencyPercentage  ?? cn.agency_percentage   ?? null;
  const net         = formatMoney(cn.netAmount   ?? cn.net_amount);
  const vat         = formatMoney(cn.vatAmount   ?? cn.vat_amount);
  const gross       = formatMoney(cn.grossAmount ?? cn.gross_amount);
  const dates       = formatDates(cn.bookingDates || cn.booking_dates || cn.invoiceDate || cn.invoice_date);
  const type        = cn.invoiceType       || cn.invoice_type        || '—';

  const agencyRow = agencyName && agencyName.trim().length > 1
    ? `\n  <strong>Agency:</strong> ${agencyName}${agencyPct != null ? ` (${Number(agencyPct)}%)` : ''}<br>`
    : '';

  return `
<p>Dear ${talentName},</p>

<p>
  a credit note has been issued for you.<br>
  Please find the Gutschrift attached to this email as a PDF (with the XRechnung XML embedded)
  for your records.
</p>

<p>
  <strong>Credit note no.:</strong> ${number}<br>
  <strong>Type:</strong> ${type}<br>
  <strong>Talent:</strong> ${talentName}<br>${agencyRow}
  <strong>Working date(s):</strong> ${dates}
</p>

<p>
  <strong>Net:</strong> ${net} €<br>
  <strong>VAT:</strong> ${vat} €<br>
  <strong>Gross total:</strong> ${gross} €
</p>

<p>If you have any questions about this credit note, please contact us at talents@set-cast.com.</p>

<p>Thank you for working with us.</p>
`.trim();
}

/**
 * Builds the HTML body for a CANCELLATION (Stornogutschrift) notification email.
 *
 * Sent when a credit note is cancelled. The talent already holds the original document, so it
 * cannot simply be withdrawn — it is reversed by this Storno, and any correction arrives as a
 * separate credit note with a new number. The amounts here are negative on purpose: that is
 * what reverses the original entry.
 *
 * @param {object} cn - The STORNO credit note record (invoice_type 'storno').
 * @returns {string} HTML string
 */
export function buildCreditNoteCancellationEmailHtml(cn) {
  const number      = cn.creditNoteNumber  || cn.credit_note_number  || '—';
  const talentName  = cn.talentName        || cn.talent_name         || '—';
  const net         = formatMoney(cn.netAmount   ?? cn.net_amount);
  const vat         = formatMoney(cn.vatAmount   ?? cn.vat_amount);
  const gross       = formatMoney(cn.grossAmount ?? cn.gross_amount);
  const dates       = formatDates(cn.bookingDates || cn.booking_dates || cn.invoiceDate || cn.invoice_date);
  // The backend numbers a Storno "<original>-S", so the original is recoverable from it.
  const original    = originalNumberOf(number);

  return `
<p>Dear ${talentName},</p>

<p>
  the credit note${original ? ` <strong>${original}</strong>` : ''} has been cancelled.<br>
  Please find the cancellation credit note (Stornogutschrift) attached to this email as a PDF
  for your records. It reverses the original document, which is no longer valid.
</p>

<p>
  <strong>Cancellation credit note no.:</strong> ${number}<br>
  ${original ? `<strong>Cancels credit note:</strong> ${original}<br>` : ''}
  <strong>Talent:</strong> ${talentName}<br>
  <strong>Working date(s):</strong> ${dates}
</p>

<p>
  <strong>Net:</strong> ${net} €<br>
  <strong>VAT:</strong> ${vat} €<br>
  <strong>Gross total:</strong> ${gross} €
</p>

<p>
  If the job still needs to be invoiced, you will receive a new credit note with a new number.
  Please use that one instead of the cancelled document.
</p>

<p>If you have any questions about this cancellation, please contact us at talents@set-cast.com.</p>

<p>Thank you for working with us.</p>
`.trim();
}

/** "SCTCN260800042-S" → "SCTCN260800042"; anything without the suffix yields null. */
function originalNumberOf(stornoNumber) {
  const s = String(stornoNumber || '').trim();
  return s.endsWith('-S') ? s.slice(0, -2) : null;
}

function formatMoney(n) {
  if (n == null || n === '') return '—';
  const num = typeof n === 'number' ? n : parseFloat(n);
  return isNaN(num) ? String(n) : num.toFixed(2);
}

/**
 * Working date(s) as dd.mm.yyyy. The API returns ISO timestamps, which used to be printed raw
 * in the email ("2026-08-05T00:00:00.000Z"). Accepts an array, a comma-separated string or a
 * single value, and leaves anything unparseable alone rather than inventing a date.
 */
function formatDates(value) {
  if (value == null || value === '') return '—';
  const one = (v) => {
    const s = String(v).trim();
    if (!s) return null;
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
  };
  const parts = (Array.isArray(value) ? value : String(value).split(','))
    .map(one)
    .filter(Boolean);
  const unique = [...new Set(parts)];
  return unique.length ? unique.join('<br>') : '—';
}
